import { c as _c } from "react/compiler-runtime";
import React, { useReducer, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import { LocalizedNameFields, PhoneInput, SelectInput, TextInput, TextareaInput } from '@/components/inputs';
import { addPrefixToDataParams } from '@/utils/string';
import { Button } from '@/components/buttons';
import { required, email, phone, pipeValidators } from '@/utils/validators';
import { useInitialValues } from '@/hooks/useInitialValues';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import { WithdrawalModal } from './WithdrawalModal';
import { useCustomerRequestCreateQuery } from '@/api/skippay/customerRequestCreateQuery';
import Loader from '@/components/Loader';
import { SuccessMessage } from './SuccessMessage';
import { operations } from '@/api/skippay/generated/selfCare';
import { FieldValidator } from 'final-form';
import IconMail from '@/assets/icons/heroIcons/Mail.svg';
import { logEvent } from '@/analytics/events';
import csTranslations from '@/lang/cs/personalDataRequests.json';
const FIELD_EMAIL = 'email';
const FIELD_FIRST_NAME = 'firstName';
const FIELD_LAST_NAME = 'lastName';
const FIELD_MESSAGE = 'message';
const FIELD_PHONE = 'phoneNumber';
const FIELD_SUBJECT = 'subject';
type FormValues = {
  [FIELD_EMAIL]: string;
  [FIELD_FIRST_NAME]: string;
  [FIELD_LAST_NAME]: string;
  [FIELD_MESSAGE]: string;
  [FIELD_PHONE]: string;
  [FIELD_SUBJECT]: string;
};
type StatusOptionsData = {
  options: {
    label: string;
    value: string;
  }[];
  messageRequiredByLabel: Record<string, boolean>;
  messagePlaceholderByLabel: Record<string, string | undefined>;
};
enum CustomerRequestStatusEnum {
  ACCEPTED = 'ACCEPTED',
  UNCONFIRMED = 'UNCONFIRMED',
}
type CustomerRequestBody = operations['customerRequestCreate']['requestBody']['content']['application/json'];
const csOpts = csTranslations.personalDataRequests.form.fields.interest.options;
export const PersonalDataForm = () => {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["personalDataRequests", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const router = useRouter();
  const [withdrawalDontCall, setWithdrawalDontCall] = useState(false);
  const contractPlusWithdrawal = withdrawalDontCall ? t("form.fields.interest.options.9.dontCall") : t("form.fields.interest.options.9");
  const requestDeletePersonalData = t("form.fields.interest.options.6");
  const repaymentSchedule = t("form.fields.interest.options.1");
  const manualReview = t("form.fields.interest.options.11");
  const initialValues = useInitialValues();
  const customerRequestQuery = useCustomerRequestCreateQuery();
  const selectedOption = router.query.s || initialValues?.subject;
  const isCsLanguage = useIsSelectedLanguage("cs");
  const statusOptions = [selectedOption === csOpts["1"] && {
    label: repaymentSchedule,
    value: csOpts["1"],
    messageRequired: false
  }, selectedOption === csOpts["11"] && {
    label: manualReview,
    value: csOpts["11"],
    messageRequired: false
  }, {
    label: t("form.fields.interest.options.2"),
    value: csOpts["2"],
    messageRequired: false
  }, {
    label: t("form.fields.interest.options.3"),
    value: csOpts["3"],
    messageRequired: true
  }, {
    label: t("form.fields.interest.options.4"),
    value: csOpts["4"],
    messageRequired: true,
    messagePlaceholder: t("form.fields.interest.placeholder.4")
  }, {
    label: t("form.fields.interest.options.5"),
    value: csOpts["5"],
    messageRequired: true
  }, {
    label: requestDeletePersonalData,
    value: csOpts["6"],
    messageRequired: false
  }, {
    label: t("form.fields.interest.options.7"),
    value: csOpts["7"],
    messageRequired: true
  }, isCsLanguage && {
    label: t("form.fields.interest.options.8"),
    value: csOpts["8"],
    messageRequired: true
  }, isCsLanguage && {
    label: contractPlusWithdrawal,
    value: withdrawalDontCall ? csOpts["9.dontCall"] : csOpts["9"],
    messageRequired: false
  }, {
    label: t("form.fields.interest.options.10"),
    value: csOpts["10"],
    messageRequired: false
  }].filter(Boolean) as {
    label: string;
    value: string;
    messageRequired: boolean;
    messagePlaceholder?: string;
  }[];
  const selectedOptionExist = statusOptions.some(option => option.value === selectedOption);
  const {
    options,
    messageRequiredByLabel,
    messagePlaceholderByLabel
  } = statusOptions.reduce(_temp, {
    options: [],
    messageRequiredByLabel: {},
    messagePlaceholderByLabel: {}
  } as StatusOptionsData) as {
    options: {
      label: string;
      value: string;
    }[];
    messageRequiredByLabel: Record<string, boolean>;
    messagePlaceholderByLabel: Record<string, string | undefined>;
  };
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [t2, send] = useReducer(_temp2, t1);
  const {
    success,
    error
  } = t2;
  let t3;
  if ($[2] !== initialValues || $[3] !== messageRequiredByLabel) {
    t3 = (value_0, t4) => {
      const allValues = t4 === undefined ? {} : t4;
      return messageRequiredByLabel[allValues[FIELD_SUBJECT] || initialValues[FIELD_SUBJECT]] ? required(value_0) : undefined;
    };
    $[2] = initialValues;
    $[3] = messageRequiredByLabel;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const requiredMessage = t3;
  let t4;
  if ($[5] !== customerRequestQuery) {
    t4 = async data => {
      logEvent("generate_lead", {
        action: "customerData"
      });
      ;
      try {
        await customerRequestQuery.mutate(data);
        send("OK");
      } catch (t5) {
        send("KO");
      }
    };
    $[5] = customerRequestQuery;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== customerRequestQuery.data?.status || $[8] !== customerRequestQuery.isPending || $[9] !== error || $[10] !== initialValues || $[11] !== messagePlaceholderByLabel || $[12] !== messageRequiredByLabel || $[13] !== options || $[14] !== requiredMessage || $[15] !== selectedOption || $[16] !== selectedOptionExist || $[17] !== success || $[18] !== t) {
    t5 = t6 => {
      const {
        handleSubmit,
        form
      } = t6;
      const {
        values: t7
      } = form.getState();
      const {
        subject
      } = t7 === undefined ? {} : t7;
      const messageRequired_0 = messageRequiredByLabel[subject || initialValues[FIELD_SUBJECT]];
      const messagePlaceholder_0 = messagePlaceholderByLabel[subject || initialValues[FIELD_SUBJECT]];
      if (customerRequestQuery.isPending) {
        return <Loader hideLabel={true} />;
      }
      if (error) {
        return <div className="text-center"><p className="mb-6 text-sm font-semibold text-red-500 lg:text-base">{t("form.error.text")}</p><Button variant="unstyled" onClick={() => send("RESET")} analyticsParam="sendNewRequest" testId="sendNewRequest" className="text-blue-500 underline decoration-1 hover:text-blue-800" align="center">{t("form.error.cta")}</Button></div>;
      }
      if (success) {
        if (customerRequestQuery.data?.status === CustomerRequestStatusEnum.ACCEPTED) {
          return <SuccessMessage isRefund={false} />;
        }
        return <div className="flex flex-col items-center justify-center gap-6 text-center"><IconMail className="mx-auto h-10 w-10 text-blue-500" /><h3 className="text-lg font-bold lg:text-4xl">{t("form.success.title")}</h3><p>{t("form.success.text")}</p><Button variant="unstyled" onClick={() => {
            form.batch(() => {
              form.change(FIELD_SUBJECT, "");
              form.change(FIELD_MESSAGE, "");
              send("RESET");
            });
          }} analyticsParam="sendNewRequest" testId="sendNewRequest" className="text-blue-500 underline decoration-1 hover:text-blue-800">{t("form.success.cta")}</Button></div>;
      }
      return <form method="post" onSubmit={data_0 => handleSubmit(data_0)} {...addPrefixToDataParams({
        analyticsParam: "personalDataForm",
        testId: "personalDataForm",
        prefix: "form"
      })}>{subject === csOpts["9"] && <WithdrawalModal onDontCall={() => {
          setWithdrawalDontCall(true);
          form.change(FIELD_SUBJECT, csOpts["9.dontCall"]);
        }} />}<div className="mb-10 flex flex-col gap-8"><div className="flex flex-col gap-4"><p className="font-semibold">{t("form.category.interest")}</p><div><Field name={FIELD_SUBJECT} validate={required} initialValue={selectedOptionExist ? selectedOption.toString() : undefined}>{fieldRenderProps => <SelectInput hideLabel={true} {...fieldRenderProps} label={t("form.fields.interest.title")} options={options} />}</Field></div></div><div className="flex flex-col gap-4"><p className="font-semibold">{t("form.category.contact")}</p><LocalizedNameFields nameField={{
              name: FIELD_FIRST_NAME,
              label: t("core:forms.fields.name"),
              validate: required,
              initialValue: initialValues[FIELD_FIRST_NAME]
            }} surnameField={{
              name: FIELD_LAST_NAME,
              label: t("core:forms.fields.surname"),
              validate: required,
              initialValue: initialValues[FIELD_LAST_NAME]
            }} /><Field name={FIELD_EMAIL} type="email" validate={pipeValidators(required, email)} initialValue={initialValues[FIELD_EMAIL]}>{fieldRenderProps_0 => <TextInput {...fieldRenderProps_0} label={t("core:forms.fields.email")} />}</Field>{subject === csOpts["1"] && <Field name={FIELD_PHONE} type="tel" validate={pipeValidators(required, phone)} initialValue={initialValues[FIELD_PHONE]}>{fieldRenderProps_1 => <PhoneInput {...fieldRenderProps_1} label={t("core:forms.fields.phone")} />}</Field>}</div><div className="flex flex-col gap-4"><p className="font-semibold">{t(messageRequired_0 ? "form.category.messageRequired" : "form.category.message")}</p><Field name={FIELD_MESSAGE} initialValue={initialValues[FIELD_MESSAGE]} validate={requiredMessage}>{fieldRenderProps_2 => <TextareaInput {...fieldRenderProps_2} label={messagePlaceholder_0 || (messageRequired_0 ? t("form.fields.message.labelRequired") : t("form.fields.message.label"))} hideLabel={true} />}</Field></div></div><Button type="submit" analyticsParam="submitForm" testId="submitForm" variant="primary" align="center" size="lg" busy={customerRequestQuery.isPending}>{t("core:forms.fields.submit")}</Button></form>;
    };
    $[7] = customerRequestQuery.data?.status;
    $[8] = customerRequestQuery.isPending;
    $[9] = error;
    $[10] = initialValues;
    $[11] = messagePlaceholderByLabel;
    $[12] = messageRequiredByLabel;
    $[13] = options;
    $[14] = requiredMessage;
    $[15] = selectedOption;
    $[16] = selectedOptionExist;
    $[17] = success;
    $[18] = t;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4 || $[21] !== t5) {
    t6 = <div className="rounded-xl bg-white px-4 py-6 lg:rounded-4xl lg:p-12"><Form onSubmit={t4}>{t5}</Form></div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  return t6;
};
function _temp(acc, option_0) {
  if (option_0 && "label" in option_0) {
    const {
      label,
      value,
      messageRequired,
      messagePlaceholder
    } = option_0;
    acc.options.push({
      label,
      value
    });
    acc.messageRequiredByLabel[value] = messageRequired;
    if (messagePlaceholder) {
      acc.messagePlaceholderByLabel[value] = messagePlaceholder;
    }
  }
  return acc;
}
function _temp2(_, action) {
  switch (action) {
    default:
      {
        throw new Error(`Unknown action ${action}`);
      }
    case "OK":
      {
        return {
          success: true
        };
      }
    case "KO":
      {
        return {
          error: true
        };
      }
    case "RESET":
      {
        return {};
      }
  }
}